import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  BoardResponseSchema,
  InquiryListResponseSchema,
  InquirySchema,
  type BoardResponse,
  type Inquiry,
  type InquiryEventType,
  type InquirySource,
  type InquiryStage,
  type Qualification,
} from "@/api/types/inquiry";

interface UseInquiriesParams {
  stage?: InquiryStage;
  event_type?: InquiryEventType;
  source?: InquirySource;
  q?: string;
  limit?: number;
}

/** List query with cursor pagination, mirroring useContacts' convention. */
export function useInquiries(params: UseInquiriesParams = {}) {
  const { stage, event_type, source, q, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: ["inquiries", "list", { stage, event_type, source, q, limit }],
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (stage) searchParams.set("stage", stage);
      if (event_type) searchParams.set("event_type", event_type);
      if (source) searchParams.set("source", source);
      if (q?.trim()) searchParams.set("q", q.trim());
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(
        `/v2/inquiries?${searchParams.toString()}`,
      );
      return validateResponse(
        InquiryListResponseSchema,
        data,
        "GET /v2/inquiries",
      );
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 60 * 1000,
  });
}

export function useInquiry(inquiryId: string | undefined) {
  return useQuery<Inquiry>({
    queryKey: ["inquiries", "detail", inquiryId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/inquiries/${inquiryId}`);
      return validateResponse(InquirySchema, data, "GET /v2/inquiries/:id");
    },
    enabled: !!inquiryId,
  });
}

/** Kanban board projection — {columns:[{stage, items}]}. */
export function useInquiriesBoard() {
  return useQuery<BoardResponse>({
    queryKey: ["inquiries", "board"],
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/inquiries/board`);
      return validateResponse(
        BoardResponseSchema,
        data,
        "GET /v2/inquiries/board",
      );
    },
    staleTime: 15 * 1000,
  });
}

export interface InquiryCreateInput {
  contact_name: string;
  event_date?: string | null;
  event_type?: InquiryEventType | null;
  guest_count?: number | null;
  venue_name?: string | null;
  na_guests_expected?: boolean | null;
  budget_band?: string | null;
  source?: InquirySource | null;
  message?: string | null;
  email_consent?: boolean;
  sms_consent?: boolean;
}

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: ["inquiries"] });
}

export function useCreateInquiry() {
  const queryClient = useQueryClient();
  return useMutation<Inquiry, unknown, InquiryCreateInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/inquiries", input);
      return validateResponse(InquirySchema, data, "POST /v2/inquiries");
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useUpdateInquiry() {
  const queryClient = useQueryClient();
  return useMutation<
    Inquiry,
    unknown,
    { id: string } & Partial<InquiryCreateInput> & {
        qualification?: Qualification;
      }
  >({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/inquiries/${id}`, patch);
      return validateResponse(InquirySchema, data, "PATCH /v2/inquiries/:id");
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["inquiries", "detail", updated.id], updated);
      invalidateAll(queryClient);
    },
  });
}

export interface StageChangeInput {
  id: string;
  stage: InquiryStage;
  lost_reason?: string;
}

/**
 * Stage transition — optimistically moves the card between board columns so
 * drag-and-drop feels instant; rolls back (and re-fetches) on a rejected
 * mutation, e.g. the API's 422 when closed_lost is posted without a reason
 * (the caller is expected to have already collected the reason before
 * invoking this for a closed_lost drop — see LostReasonDialog).
 */
export function useUpdateInquiryStage() {
  const queryClient = useQueryClient();
  return useMutation<
    Inquiry,
    unknown,
    StageChangeInput,
    { previous?: BoardResponse }
  >({
    mutationFn: async ({ id, stage, lost_reason }) => {
      const { data } = await apiClient.post(`/v2/inquiries/${id}/stage`, {
        stage,
        ...(lost_reason ? { lost_reason } : {}),
      });
      return validateResponse(
        InquirySchema,
        data,
        "POST /v2/inquiries/:id/stage",
      );
    },
    onMutate: async ({ id, stage }) => {
      await queryClient.cancelQueries({ queryKey: ["inquiries", "board"] });
      const previous = queryClient.getQueryData<BoardResponse>([
        "inquiries",
        "board",
      ]);

      if (previous) {
        let moved: BoardResponse["columns"][number]["items"][number] | undefined;
        const withoutItem = previous.columns.map((col) => {
          const found = col.items.find((item) => item.id === id);
          if (found) moved = found;
          return { ...col, items: col.items.filter((item) => item.id !== id) };
        });

        const next: BoardResponse = {
          columns: withoutItem.map((col) =>
            col.stage === stage && moved
              ? { ...col, items: [{ ...moved, stage }, ...col.items] }
              : col,
          ),
        };

        queryClient.setQueryData(["inquiries", "board"], next);
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["inquiries", "board"], context.previous);
      }
      invalidateAll(queryClient);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["inquiries", "detail", updated.id], updated);
    },
    onSettled: () => invalidateAll(queryClient),
  });
}

export function useDeleteInquiry() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/v2/inquiries/${id}`);
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

export function useLogFirstReply() {
  const queryClient = useQueryClient();
  return useMutation<Inquiry, unknown, string>({
    mutationFn: async (id) => {
      const { data } = await apiClient.post(
        `/v2/inquiries/${id}/reply-logged`,
      );
      return validateResponse(
        InquirySchema,
        data,
        "POST /v2/inquiries/:id/reply-logged",
      );
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["inquiries", "detail", updated.id], updated);
      invalidateAll(queryClient);
    },
  });
}
